import { reactive } from 'vue';
import Notifications from './Notifications.vue';
import { useLogStore } from '../../stores/logStore';
import { getTranslation } from '../../i18nlocal.js' // import the i18n instance

// Valid bootstrap-alert types rendered by Notification.vue. Anything else
// (e.g. the recurring notify('error', ...)) is normalized to 'danger' so the
// toast is never rendered unstyled.
const VALID_TYPES = ['info', 'primary', 'danger', 'warning', 'success'];

// Flood control: at most this many visible toasts per corner (verticalAlign +
// horizontalAlign). Extras are still recorded in the log store, just not shown.
const MAX_VISIBLE = 4;

// Dedupe window: identical type+message notifications arriving within this
// window while the original toast is still on screen bump its counter instead
// of stacking a new toast.
const DEDUPE_WINDOW_MS = 2500;

// key `${type}|${message}` -> { at: Date.now() of last add, timestamp: toast Date }
const lastByKey = new Map();

const NotificationStore = {
  state: [],
  settings: {
    overlap: false,
    verticalAlign: 'top',
    horizontalAlign: 'right',
    type: 'info',
    timeout: 5000,
    closeOnClick: true,
    showClose: true
  },
  
  setOptions(options) {
    this.settings = Object.assign({}, this.settings, options);
  },
  
  removeNotification(timestamp) {
    const indexToDelete = this.state.findIndex(n => n.timestamp === timestamp);
    if (indexToDelete !== -1) {
      this.state.splice(indexToDelete, 1);
    }
  },
  
  addNotification(notification) {
    // Get log store instance
    const logStore = useLogStore();


    if (typeof notification === 'string' || notification instanceof String) {
      notification = { message: notification };
    }

    notification = Object.assign({}, this.settings, notification);

    // Normalize type: unknown types (e.g. 'error') would render an unstyled
    // alert — fall back to 'danger'
    if (VALID_TYPES.indexOf(notification.type) === -1) {
      notification.type = 'danger';
    }

    const key = notification.type + '|' + notification.message;
    const now = Date.now();

    // Dedupe: identical type+message within the window, while that toast is
    // still on screen → bump the visible counter instead of stacking a new
    // toast. Full history is still kept in the log store.
    const last = lastByKey.get(key);
    if (last && now - last.at < DEDUPE_WINDOW_MS) {
      const existing = this.state.find(n =>
        n.type === notification.type && n.message === notification.message
      );
      if (existing) {
        existing.count = (existing.count || 1) + 1;
        last.at = now; // keep grouping while the flood lasts
        logStore.addMessage({
          message: notification.message,
          type: notification.type
        });
        return;
      }
    }

    // Cap: at most MAX_VISIBLE toasts per corner — skip the newest visible
    // toast when the corner is full (still logged for the history popup)
    const inCorner = this.state.filter(n =>
      n.verticalAlign === notification.verticalAlign &&
      n.horizontalAlign === notification.horizontalAlign
    );
    if (inCorner.length >= MAX_VISIBLE) {
      logStore.addMessage({
        message: notification.message,
        type: notification.type
      });
      return;
    }

    notification.timestamp = new Date();
    notification.timestamp.setMilliseconds(
      notification.timestamp.getMilliseconds() + this.state.length
    );

    // Bound the dedupe map — only recent keys can ever match
    if (lastByKey.size > 256) {
      for (const [k, v] of lastByKey) {
        if (now - v.at > DEDUPE_WINDOW_MS) lastByKey.delete(k);
      }
    }
    lastByKey.set(key, { at: now, timestamp: notification.timestamp });

    // Add to notification state
    this.state.push(notification);

    // Also add to log store
    logStore.addMessage({
      message: notification.message,
      type: notification.type
    });
  },
  
  notify(notification) {
    if (Array.isArray(notification)) {
      notification.forEach(notificationInstance => {
        this.addNotification(notificationInstance);
      });
    } else {
      this.addNotification(notification);
    }
  }
};

const NotificationsPlugin = {
  install(app, options) {
    const notificationStore = reactive(NotificationStore);

    const notify = (notification) => {
      notificationStore.notify(notification);
    };

    app.config.globalProperties.$notify = notify;
    app.config.globalProperties.$notifications = notificationStore;
    app.component('Notifications', Notifications);
    
    if (options) {
      NotificationStore.setOptions(options);
    }
  }
};

const notificationStore = reactive(NotificationStore);

export const useNotifications = () => {
  const notify = (notification) => {
    notificationStore.notify(notification);
  };

  return {
    notify,
    notifications: notificationStore,
  };
};

/*  
  'info',
  'primary',
  'danger',
  'warning',
  'success'
  */

export const notify = async (type, message, aditionalMessage = '') => {
  const notifications = useNotifications();
  
  notifications.notify({
    message: getTranslation(message) + aditionalMessage,
    overlap: false,
    verticalAlign: 'top',
    horizontalAlign: 'right',
    type: type,
    timeout: 5000,
    closeOnClick: true,
    showClose: false
  });
};


export default NotificationsPlugin;